/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useMemo, useState } from 'react';

import { jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags';

import { useMenuControl } from '../../../context';
import { useUniversalCreateAnalytics } from '../../../hooks';
import { withIntlProvider } from '../../../services';
import type { RenderableMenuItem, RenderableMenuSectionData } from '../../../types/menuV2';
import MenuItemV2, { isNativeRenderableMenuItem } from '../MenuItemV2';
import MenuSectionPrimitive, { MIN_ITEMS } from '../MenuSectionPrimitive';

const MAX_ITEMS = 10;

export interface RenderableMenuSectionProps {
	section: RenderableMenuSectionData;
	sectionIndex: number;
	sectionStartIndex: number;
	totalMenuItemsCount: number;
}

function RenderableMenuSectionInner({
	section,
	sectionIndex,
	totalMenuItemsCount,
	sectionStartIndex,
}: RenderableMenuSectionProps) {
	const [showAll, setShowAll] = useState(false);
	const { fireNativeMenuItemClicked, fireNewNativeMenuItemClicked } = useUniversalCreateAnalytics();
	const { product } = useMenuControl();

	const sectionItems = section.items;
	const sectionItemsCount = sectionItems.length;

	const items = useMemo(() => {
		if (!sectionItemsCount) {
			return [];
		}

		if (!section.foldable || sectionItemsCount <= MIN_ITEMS) {
			return sectionItems;
		}

		return sectionItems.slice(0, showAll ? MAX_ITEMS : MIN_ITEMS);
	}, [section.foldable, sectionItems, showAll, sectionItemsCount]);

	const toggleShowMore = useCallback(() => {
		setShowAll((prev) => !prev);
	}, [setShowAll]);

	// TODO: push it into menu item in https://hello.jira.atlassian.cloud/browse/NAVX-2530
	const menuItemWithAnalytics = useCallback(
		(item: RenderableMenuItem, itemIndex: number): RenderableMenuItem => {
			// Only add analytics for native items
			if (!isNativeRenderableMenuItem(item)) {
				return item;
			}

			const fireAnalytics = (isRightClick: boolean) => {
				const itemAti = item.ati.toString();
				if (fg('navx-uc-unified-instrumentation')) {
					fireNewNativeMenuItemClicked({
						ati: itemAti,
						menuItemsCount: totalMenuItemsCount, // Total number of items in the native sections part of menu
						menuItemIndex: sectionStartIndex + itemIndex, // Position of the item in the native sections part of menu
						groupItemProduct: product,
						groupItemsCount: sectionItemsCount, // Number of items in the section
						groupItemsIndex: itemIndex, // Position of the item in the section
						isRightClick,
					});
				} else {
					fireNativeMenuItemClicked({ ati: itemAti });
				}
			};

			return {
				...item,
				onClick: (event: React.MouseEvent | React.KeyboardEvent) => {
					fireAnalytics(false);
					item.onClick?.(event);
				},
				onContextMenu: (event: React.MouseEvent) => {
					fireAnalytics(true);
					item.onContextMenu?.(event);
				},
			};
		},
		[
			fireNativeMenuItemClicked,
			fireNewNativeMenuItemClicked,
			totalMenuItemsCount,
			sectionStartIndex,
			sectionItemsCount,
			product,
		],
	);

	if (!sectionItems?.length) {
		return null;
	}

	return (
		<MenuSectionPrimitive
			sectionIndex={sectionIndex}
			itemLength={sectionItems.length}
			foldable={section.foldable}
			heading={section.heading}
			showAll={showAll}
			toggleShowMore={toggleShowMore}
			isNative
		>
			{items.map((menuItem: RenderableMenuItem, index: number) => (
				<MenuItemV2
					item={menuItemWithAnalytics(menuItem, index)}
					key={`menu-section-${sectionIndex}-item-${index}`}
				/>
			))}
		</MenuSectionPrimitive>
	);
}

export const RenderableMenuSection = withIntlProvider(RenderableMenuSectionInner);
